import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowDownTrayIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { toast } from "sonner";
import { uploadRequiredDocuments, uploadSingleFile } from "../../services/api";
import { getOutcomeRegistryRows } from "../../utils/outcomeRegistryHelpers";

const toTitleCase = (str: string): string =>
  String(str)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();
  
const flattenTranslationValues = (
  obj: Record<string, any>,
  result: Record<string, string> = {},
) => {
  Object.entries(obj).forEach(([key, value]) => {
    if (typeof value === "string") {
      result[value] = key;
    } else if (value && typeof value === "object") {
      flattenTranslationValues(value, result);
    }
  });

  return result;
};

type TemplateDisplayMeta = {
  stepLabels: Record<string, string>;
  fieldLabels: Record<string, string>;
  fieldTypes: Record<string, string>;
  optionLabels: Record<string, Record<string, string>>;
};

const emptyTemplateDisplayMeta: TemplateDisplayMeta = {
  stepLabels: {},
  fieldLabels: {},
  fieldTypes: {},
  optionLabels: {},
};

const getTemplateStepId = (step: any): string =>
  String(
    step?.stepId ||
      step?.id ||
      step?.key ||
      String(step?.title || "step")
        .toLowerCase()
        .replace(/\s+/g, "_"),
  );

const isObjectValue = (value: any): value is Record<string, any> =>
  !!value && typeof value === "object" && !Array.isArray(value);

const getFirstValue = (...values: any[]) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const isHttpUrl = (value: string): boolean => /^https?:\/\//i.test(value);

const getFileNameFromUrl = (value: string): string => {
  try {
    const url = new URL(value);
    return decodeURIComponent(
      url.pathname.split("/").filter(Boolean).pop() || "",
    );
  } catch {
    return decodeURIComponent(value.split("/").pop()?.split("?")[0] || "");
  }
};

const FILE_UPLOAD_TYPES = new Set(["file", "file_upload", "document_upload"]);

const FILE_EXTENSION_REGEX =
  /\.(pdf|doc|docx|xls|xlsx|csv|txt|rtf|zip|rar|jpg|jpeg|png|gif|webp|mp4|mov|avi|wmv|mp3|wav)$/i;

type FileDisplayItem = {
  name: string;
  url: string;
  extension: string;
};

const getFileExtension = (fileName: string): string => {
  const match = fileName.match(/\.([a-z0-9]+)$/i);
  return match?.[1]?.toUpperCase() || "";
};

const getFileDisplayName = (value: string): string =>
  getFileNameFromUrl(value) || value || "file";

const normalizeFileItems = (value: any): FileDisplayItem[] => {
  if (Array.isArray(value)) return value.flatMap(normalizeFileItems);

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (!trimmedValue) return [];

    const name = getFileDisplayName(trimmedValue);
    return [
      {
        name,
        url: isHttpUrl(trimmedValue) ? trimmedValue : "",
        extension: getFileExtension(name),
      },
    ];
  }

  if (isObjectValue(value)) {
    const rawUrl = getFirstValue(
      value.fileUrl,
      value.url,
      value.link,
      value.href,
      value.path,
    );
    const rawName = getFirstValue(
      value.fileName,
      value.name,
      value.originalFileName,
      rawUrl,
    );

    if (!rawUrl && !rawName) return [];

    const url =
      typeof rawUrl === "string" && isHttpUrl(rawUrl.trim())
        ? rawUrl.trim()
        : "";
    const nameSource =
      typeof rawName === "string" ? rawName.trim() : String(rawName || "");
    const name = getFileDisplayName(nameSource || url);

    return [
      {
        name,
        url,
        extension: getFileExtension(name),
      },
    ];
  }

  return [];
};

const hasFileExtension = (file: FileDisplayItem): boolean =>
  FILE_EXTENSION_REGEX.test(file.name) ||
  (!!file.url && FILE_EXTENSION_REGEX.test(getFileNameFromUrl(file.url)));

const downloadFileFromUrl = async (url: string, fileName: string) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error("Download failed");

  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);

  try {
    const anchorElement = document.createElement("a");
    anchorElement.href = downloadUrl;
    anchorElement.download = fileName || getFileNameFromUrl(url) || "download";
    anchorElement.style.display = "none";
    document.body.appendChild(anchorElement);
    anchorElement.click();
    anchorElement.remove();
  } finally {
    window.setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 1000);
  }
};

const getFirstFieldEntry = (
  ...entries: Array<{ key: string; value: any; source?: any }>
) =>
  entries.find(
    (entry) =>
      entry.value !== undefined && entry.value !== null && entry.value !== "",
  );

const getLookupValue = (source: any, key: string) => {
  if (!isObjectValue(source) || !key) return undefined;
  if (source[key] !== undefined) return source[key];

  const matchedKey = Object.keys(source).find(
    (sourceKey) => sourceKey.toLowerCase() === key.toLowerCase(),
  );

  return matchedKey ? source[matchedKey] : undefined;
};

const getLabelFromSource = (source: any, key: string): string | undefined => {
  if (!isObjectValue(source) || !key) return undefined;

  const directLabel = getFirstValue(
    source[`${key}Label`],
    source[`${key}Title`],
    source[`${key}Name`],
  );

  if (directLabel) return String(directLabel);

  const labelMaps = [
    source.labels,
    source.fieldLabels,
    source.displayLabels,
    source.policyLabels,
  ];

  for (const labelMap of labelMaps) {
    const mappedLabel = getLookupValue(labelMap, key);
    if (mappedLabel) return String(mappedLabel);
  }

  return undefined;
};

const getValueLabelFromSource = (
  source: any,
  key: string,
  value: any,
): string | undefined => {
  if (!isObjectValue(source) || !key) return undefined;

  const valueKey = value === undefined || value === null ? "" : String(value);
  const directLabel = getFirstValue(
    source[`${key}DisplayLabel`],
    source[`${key}DisplayText`],
    source[`${key}ValueLabel`],
    source[`${key}Text`],
    source[`${key}Label`],
  );

  if (directLabel) return String(directLabel);

  const labelMaps = [
    source.valueLabels?.[key],
    source.optionLabels?.[key],
    source[`${key}Labels`],
    source[`${key}Options`],
  ];

  for (const labelMap of labelMaps) {
    if (Array.isArray(labelMap)) {
      const matchedOption = labelMap.find((option: any) => {
        const optionValue = getTemplateOptionValue(option);
        return optionValue.toLowerCase() === valueKey.toLowerCase();
      });
      const optionLabel = matchedOption
        ? getTemplateOptionLabel(matchedOption)
        : "";
      if (optionLabel) return optionLabel;
      continue;
    }

    const mappedLabel = getLookupValue(labelMap, valueKey);
    if (mappedLabel) return String(mappedLabel);
  }

  return undefined;
};

const getPolicyOutcomeSource = (stepData: any) => {
  if (!stepData) return {};
  if (Array.isArray(stepData)) return stepData;
  if (Array.isArray(stepData.outcomes) || isObjectValue(stepData.outcomes)) {
    return stepData.outcomes;
  }
  if (Array.isArray(stepData.scenarios) || isObjectValue(stepData.scenarios)) {
    return stepData.scenarios;
  }
  return stepData;
};

const getPolicyValueKey = (value: any, fallback: string) => {
  if (!isObjectValue(value)) return fallback;
  return String(
    value.key ??
      value.scenario ??
      value.outcome ??
      value.outcomeKey ??
      value.name ??
      fallback,
  );
};

const getPolicyValueLabel = (value: any, fallback: string) => {
  if (!isObjectValue(value)) return toTitleCase(fallback);
  return String(
    value.label ??
      value.scenarioLabel ??
      value.title ??
      value.name ??
      toTitleCase(fallback),
  );
};

const getPolicyValueByKey = (source: any, key: string) => {
  if (Array.isArray(source)) {
    return source.find((item) => {
      const itemKey = getPolicyValueKey(item, "");
      return itemKey.toLowerCase() === key.toLowerCase();
    });
  }

  if (!isObjectValue(source)) return undefined;
  if (source[key] !== undefined) return source[key];

  const matchedKey = Object.keys(source).find(
    (itemKey) => itemKey.toLowerCase() === key.toLowerCase(),
  );
  return matchedKey ? source[matchedKey] : undefined;
};

const getPolicyCardsFromSource = (source: any) => {
  if (Array.isArray(source)) {
    return source.map((item, index) => {
      const key = getPolicyValueKey(item, `policy-${index + 1}`);
      return {
        key,
        label: getPolicyValueLabel(item, key),
        group: "",
        meta: {},
        value: isObjectValue(item) ? item : { value: item },
      };
    });
  }

  if (!isObjectValue(source)) return [];

  return Object.entries(source).map(([key, value]) => ({
    key,
    label: getPolicyValueLabel(value, key),
    group: "",
    meta: {},
    value: isObjectValue(value) ? value : { value },
  }));
};

const getPayoutPolicyCards = (stepData: any, step?: any) => {
  const source = getPolicyOutcomeSource(stepData);
  const registryRows = step?.outcomeRegistry
    ? getOutcomeRegistryRows(step.outcomeRegistry)
    : [];

  if (registryRows.length === 0) {
    return getPolicyCardsFromSource(source);
  }

  const cards = registryRows
    .filter((row) => row?.key)
    .map((row) => {
      const value = getPolicyValueByKey(source, row.key) || {};
      return {
        key: row.key,
        label: row.label || toTitleCase(row.key),
        group: row.group || "",
        meta: row,
        value: isObjectValue(value) ? value : { value },
      };
    });

  const registryKeys = new Set(cards.map((card) => card.key.toLowerCase()));
  const extraCards = getPolicyCardsFromSource(source).filter(
    (card) => !registryKeys.has(card.key.toLowerCase()),
  );

  return [...cards, ...extraCards];
};

const getComponentValueKey = (component: any) => {
  if (!isObjectValue(component)) return String(component ?? "");
  return String(
    component.componentType ??
      component.type ??
      component.key ??
      component.value ??
      component.name ??
      component.label ??
      "",
  );
};

const getTemplateOptionValue = (option: any): string => {
  if (typeof option === "string") return option;
  return String(option?.value ?? option?.key ?? option?.id ?? "");
};

const getTemplateOptionLabel = (option: any): string => {
  if (typeof option === "string") return option;
  return String(
    option?.label ??
      option?.title ??
      option?.text ??
      option?.name ??
      option?.value ??
      option?.key ??
      "",
  );
};

const addOptionLabel = (
  meta: TemplateDisplayMeta,
  fieldPath: string,
  value: string,
  label: string,
) => {
  if (!value || !label) return;
  if (!meta.optionLabels[fieldPath]) meta.optionLabels[fieldPath] = {};
  meta.optionLabels[fieldPath][value] = label;
  meta.optionLabels[fieldPath][value.toLowerCase()] = label;
  meta.optionLabels[fieldPath][value.toUpperCase()] = label;
};

const buildTemplateDisplayMeta = (templateJson?: any): TemplateDisplayMeta => {
  const steps = Array.isArray(templateJson?.steps) ? templateJson.steps : [];
  if (steps.length === 0) return emptyTemplateDisplayMeta;

  const meta: TemplateDisplayMeta = {
    stepLabels: {},
    fieldLabels: {},
    fieldTypes: {},
    optionLabels: {},
  };
  const optionSourceRefs: Array<{ fieldPath: string; sourcePath: string }> = [];

  const normalizeSourcePath = (sourcePath: string, stepId: string): string => {
    const trimmed = sourcePath.trim();
    return trimmed.includes(".") ? trimmed : `${stepId}.${trimmed}`;
  };

  const registerField = (field: any, fieldPath: string, stepId: string) => {
    const label = field?.label ?? field?.title ?? field?.name;
    if (label) meta.fieldLabels[fieldPath] = String(label);
    if (field?.type) meta.fieldTypes[fieldPath] = String(field.type);

    if (Array.isArray(field?.options)) {
      field.options.forEach((option: any) => {
        addOptionLabel(
          meta,
          fieldPath,
          getTemplateOptionValue(option),
          getTemplateOptionLabel(option),
        );
      });
    }

    if (field?.optionsFrom) {
      optionSourceRefs.push({
        fieldPath,
        sourcePath: normalizeSourcePath(String(field.optionsFrom), stepId),
      });
    }

    if (Array.isArray(field?.fields)) {
      field.fields.forEach((nestedField: any) => {
        const nestedKey = String(nestedField?.key || "");
        if (!nestedKey) return;
        registerField(nestedField, `${fieldPath}.${nestedKey}`, stepId);
      });
    }
  };

  steps.forEach((step: any) => {
    const stepId = getTemplateStepId(step);
    const stepLabel = step?.title ?? step?.label ?? step?.name;
    if (stepLabel) meta.stepLabels[stepId] = String(stepLabel);

    if (Array.isArray(step?.fields)) {
      step.fields.forEach((field: any) => {
        const fieldKey = String(field?.key || "");
        if (!fieldKey) return;
        registerField(field, `${stepId}.${fieldKey}`, stepId);
      });
    }
  });

  optionSourceRefs.forEach(({ fieldPath, sourcePath }) => {
    const sourceLabels = meta.optionLabels[sourcePath];
    if (!sourceLabels) return;
    meta.optionLabels[fieldPath] = {
      ...sourceLabels,
      ...(meta.optionLabels[fieldPath] ?? {}),
    };
  });

  return meta;
};

type UseDealViewRenderersArgs = {
  deal: any;
  formData: any;
  t: any;
  refreshDeal?: () => Promise<void>;
};

export const useDealViewRenderers = ({
  deal,
  formData,
  t,
  refreshDeal,
}: UseDealViewRenderersArgs) => {
   const { i18n } = useTranslation();
  const [downloadingFileKey, setDownloadingFileKey] = useState<string | null>(
    null,
  );

  const templateSteps = useMemo(() => {
    const steps = deal?.templateJson?.steps;
    return Array.isArray(steps) ? steps : [];
  }, [deal?.templateJson]);

  const [selectedFilesByKey, setSelectedFilesByKey] = useState<
    Record<string, File | null>
  >({});
  const [uploadingByKey, setUploadingByKey] = useState<
    Record<string, boolean>
  >({});

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("loginResponse") || "{}");
    } catch {
      return {};
    }
  }, []);

  const currentUserId = String(currentUser?.id || "").toLowerCase();
  const currentUsername = String(currentUser?.username || "").toLowerCase();

  const acceptedBy = deal?.acceptedBy || {};
  const acceptedById = String(
    acceptedBy?.id || acceptedBy?.profileDto?.id || acceptedBy?.profile?.id || "",
  ).toLowerCase();
  const acceptedByUsername = String(
    acceptedBy?.username ||
      acceptedBy?.profileDto?.username ||
      acceptedBy?.profile?.username ||
      "",
  ).toLowerCase();

  const isAcceptedUser =
    (currentUserId && acceptedById && currentUserId === acceptedById) ||
    (currentUsername && acceptedByUsername &&
      currentUsername === acceptedByUsername);

  // Document upload must happen BEFORE the deal is accepted: the backend
  // rejects the accept action itself with 400 ("Deal cannot be accepted.
  // Pending/missed document uploads: ...") if any required document is not
  // yet uploaded. So the upload window is while the deal is still PROPOSED,
  // and `acceptedBy` here represents the fighter the deal was proposed to
  // (the intended accepter), not necessarily someone who has already accepted.
  const DOCUMENT_UPLOAD_ALLOWED_STATUSES = new Set(["PROPOSED"]);

  const isDocumentUploadAllowed =
    isAcceptedUser &&
    DOCUMENT_UPLOAD_ALLOWED_STATUSES.has(String(deal?.status || ""));

  const getItemKey = (baseKey: string, index: number) =>
    `${baseKey}-${index}`;

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, itemKey: string) => {
      const file = event.target.files?.[0] ?? null;

      if (file) {
        const isPdfType = file.type === "application/pdf";
        const isPdfExtension = /\.pdf$/i.test(file.name);

        if (!isPdfType && !isPdfExtension) {
          toast.error(t("onlyPdfAllowed", { defaultValue: "Only PDF files are allowed for this document." }));
          event.target.value = "";
          setSelectedFilesByKey((prev) => ({ ...prev, [itemKey]: null }));
          return;
        }
      }

      setSelectedFilesByKey((prev) => ({ ...prev, [itemKey]: file }));
    },
    [t],
  );

  const handleUploadDocument = useCallback(
    async (itemKey: string, documentType: string) => {
      const selectedFile = selectedFilesByKey[itemKey];

      if (!selectedFile) {
        toast.error(t("pleaseChooseFileFirst", { defaultValue: "Please choose a file first." }));
        return;
      }

      const isPdfType = selectedFile.type === "application/pdf";
      const isPdfExtension = /\.pdf$/i.test(selectedFile.name);
      if (!isPdfType && !isPdfExtension) {
        toast.error(t("onlyPdfAllowed", { defaultValue: "Only PDF files are allowed for this document." }));
        return;
      }

      if (!deal?.id) {
        toast.error(t("dealIdMissing", { defaultValue: "Deal id is missing." }));
        return;
      }

      if (!isDocumentUploadAllowed) {
        toast.error(
          t("documentUploadNotAllowed", {
            defaultValue:
              "Document upload is only allowed for the deal accepter while the deal is still in PROPOSED status (documents must be uploaded before you can accept the deal).",
          }),
        );
        return;
      }

      setUploadingByKey((prev) => ({ ...prev, [itemKey]: true }));

      try {
        const uploadedFile = await uploadSingleFile(selectedFile);
        const request = {
          dealId: String(deal.id),
          documents: [
            {
              documentType,
              fileUrl: uploadedFile.fileUrl,
              fileName: uploadedFile.fileName,
              fileType: uploadedFile.fileType,
              fileSize: uploadedFile.fileSize,
            },
          ],
        };

        await uploadRequiredDocuments(request);

        toast.success(t("documentUploadedSuccessfully", { defaultValue: "Document uploaded successfully." }));
        setSelectedFilesByKey((prev) => ({ ...prev, [itemKey]: null }));

        if (typeof refreshDeal === "function") {
          await refreshDeal();
        }
      } catch (error: any) {
        toast.error(error?.message || t("unableToUploadDocument", { defaultValue: "Unable to upload document." }));
      } finally {
        setUploadingByKey((prev) => ({ ...prev, [itemKey]: false }));
      }
    },
    [deal?.id, isDocumentUploadAllowed, refreshDeal, selectedFilesByKey],
  );

const templateDisplayMeta = useMemo(
  () => buildTemplateDisplayMeta(deal?.templateJson),
  [deal?.templateJson],
);

const reverseTranslationMap = useMemo(() => {
  const bundle =
    i18n.getResourceBundle("en", "viewDeal") || {};

  return flattenTranslationValues(bundle);
}, [i18n]);

const tr = useCallback(
  (value?: string) => {
    if (!value) return "";

    const translated = t(value);

    if (translated !== value) {
      return translated;
    }

    const translationKey = reverseTranslationMap[value];

    if (translationKey) {
      return t(translationKey);
    }

    return value;
  },
  [reverseTranslationMap, t],
);

const formatFieldLabel = useCallback(
  (key: string, fieldPath = key) =>
    tr(
      templateDisplayMeta.fieldLabels[fieldPath] ||
        templateDisplayMeta.stepLabels[fieldPath] ||
        toTitleCase(key),
    ),
  [templateDisplayMeta, tr],
);

const getOptionLabelForValue = useCallback(
  (fieldPath: string | undefined, value: string): string | undefined => {
    if (!fieldPath) return undefined;

    const labels = templateDisplayMeta.optionLabels[fieldPath];
    if (!labels) return undefined;

    const label =
      labels[value] ||
      labels[value.toLowerCase()] ||
      labels[value.toUpperCase()];

    return label ? tr(label) : undefined;
  },
  [templateDisplayMeta, tr],
);
  const isFileUploadField = useCallback(
    (fieldPath?: string): boolean => {
      if (!fieldPath) return false;

      const fieldType = templateDisplayMeta.fieldTypes[fieldPath]?.toLowerCase();
      if (fieldType && FILE_UPLOAD_TYPES.has(fieldType)) return true;

      const fieldKey = fieldPath.split(".").pop() || "";
      return /file|document|attachment/i.test(fieldKey);
    },
    [templateDisplayMeta],
  );

  const shouldRenderFileValue = useCallback(
    (value: any, fieldPath?: string): boolean => {
      const files = normalizeFileItems(value);
      if (files.length === 0) return false;
      if (isFileUploadField(fieldPath)) return true;
      return files.some(hasFileExtension);
    },
    [isFileUploadField],
  );

  const handleDownloadFile = useCallback(
    async (file: FileDisplayItem, fileKey: string) => {
      if (!file.url || downloadingFileKey === fileKey) return;

      setDownloadingFileKey(fileKey);
      try {
        await downloadFileFromUrl(file.url, file.name);
      } catch {
        toast.error(
          t("downloadFailed", {
            defaultValue: "Download failed. Please try again.",
          }),
        );
      } finally {
        setDownloadingFileKey(null);
      }
    },
    [downloadingFileKey, t],
  );

  const renderFileValue = useCallback(
    (value: any): React.ReactNode => {
      const files = normalizeFileItems(value);
      if (files.length === 0) return "-";

      return (
        <div className="max-w-full space-y-2">
          {files.map((file, index) => {
            const fileName = file.name
              ? hasFileExtension(file)
                ? file.name
                : toTitleCase(file.name)
              : t("viewFile", { defaultValue: "View file" });
            const fileType =
              file.extension || t("document", { defaultValue: "Document" });
            const fileKey = `${file.url || fileName}-${index}`;
            const isDownloading = downloadingFileKey === fileKey;

            return (
              <div
                key={fileKey}
                className="flex min-w-0 items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2 shadow-sm"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-blue-50 text-blue-600">
                  <DocumentTextIcon className="h-5 w-5" />
                </div>

                <div className="min-w-0 flex-1">
                  {file.url ? (
                    <button
                      type="button"
                      disabled={isDownloading}
                      onClick={() => void handleDownloadFile(file, fileKey)}
                      className="block w-full truncate text-left text-sm font-semibold text-gray-900 hover:text-blue-700 disabled:cursor-wait disabled:text-gray-500"
                      title={fileName}
                    >
                      {fileName}
                    </button>
                  ) : (
                    <p
                      className="truncate text-sm font-semibold text-gray-900"
                      title={fileName}
                    >
                      {fileName}
                    </p>
                  )}
                  <p className="text-[11px] font-medium text-gray-500">
                    {fileType}
                  </p>
                </div>

                {file.url && (
                  <button
                    type="button"
                    disabled={isDownloading}
                    onClick={() => void handleDownloadFile(file, fileKey)}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 disabled:cursor-wait disabled:opacity-60"
                    aria-label={t("downloadFile", {
                      fileName,
                      defaultValue: `Download ${fileName}`,
                    })}
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    <span>
                      {isDownloading
                        ? t("downloading", { defaultValue: "Downloading" })
                        : t("download", { defaultValue: "Download" })}
                    </span>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      );
    },
    [downloadingFileKey, handleDownloadFile, t],
  );

  const formatFieldValue = useCallback(
    (value: any, fieldPath?: string): React.ReactNode => {
      if (value === null || value === undefined) return "-";
      if (shouldRenderFileValue(value, fieldPath)) return renderFileValue(value);
      if (typeof value === "boolean") return value ? t("yes") : t("no");
      if (typeof value === "number") return String(value);

      if (typeof value === "string") {
        const trimmedValue = value.trim();
        const optionLabel = getOptionLabelForValue(fieldPath, trimmedValue);
        if (optionLabel) return optionLabel;
        if (!trimmedValue) return "-";
        if (isHttpUrl(trimmedValue)) {
          const fileName = getFileNameFromUrl(trimmedValue);
          return (
            <a
              href={trimmedValue}
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-blue-600 hover:text-blue-700"
              title={trimmedValue}
            >
              {fileName || t("viewFile", { defaultValue: "View file" })}
            </a>
          );
        }
       if (trimmedValue.startsWith("{") && trimmedValue.endsWith("}")) {
  return tr(toTitleCase(trimmedValue.slice(1, -1).trim()));
}
        return tr(toTitleCase(trimmedValue));
      }

      if (Array.isArray(value)) {
        if (value.length === 0) return "-";
        return value
          .map((item) =>
            typeof item === "string"
              ? isHttpUrl(item.trim())
                ? getFileNameFromUrl(item.trim()) || item
                : formatFieldValue(item, fieldPath)
              : typeof item === "number"
                ? String(item)
                : typeof item === "boolean"
                  ? item
                    ? t("yes")
                    : t("no")
                  : "-",
          )
          .filter(Boolean)
          .join(", ");
      }

      if (typeof value === "object") {
        const entries = Object.entries(value);
        const previewParts = entries
          .filter(([, entryValue]) => entryValue !== null && entryValue !== undefined)
          .slice(0, 4)
          .map(([entryKey, entryValue]) => {
            const entryPath = fieldPath ? `${fieldPath}.${entryKey}` : entryKey;
            if (Array.isArray(entryValue)) {
              const arr = entryValue
                .map((x) =>
                  typeof x === "string"
                    ? formatFieldValue(x, entryPath)
                    : typeof x === "number"
                      ? String(x)
                      : "-",
                )
                .join(", ");
              return `${formatFieldLabel(entryKey, entryPath)}: ${arr}`;
            }

            if (typeof entryValue === "object") {
              return `${formatFieldLabel(entryKey, entryPath)}: {...}`;
            }

            return `${formatFieldLabel(entryKey, entryPath)}: ${formatFieldValue(entryValue, entryPath)}`;
          });

        return previewParts.length > 0 ? previewParts.join(", ") : "-";
      }

      return String(value);
    },
    [
      formatFieldLabel,
      getOptionLabelForValue,
      renderFileValue,
      shouldRenderFileValue,
      t,
    ],
  );

  const policySummaryByScenario = useMemo(() => {
    const summaryMap: Record<string, any> = {};
    const scenarios = deal?.dealPolicySummaryDto?.scenarios;

    if (!Array.isArray(scenarios)) return summaryMap;

    scenarios.forEach((scenario: any) => {
      [scenario?.scenario, scenario?.scenarioLabel].forEach((key) => {
        if (!key) return;
        summaryMap[String(key).toLowerCase()] = scenario;
      });
    });

    return summaryMap;
  }, [deal?.dealPolicySummaryDto?.scenarios]);

  const getComponentLabel = useCallback(
    (component: any): string => {
      const componentKey = getComponentValueKey(component);

      if (componentKey) {
        const templateLabel = getOptionLabelForValue(
          "components.componentType",
          componentKey,
        );
        if (templateLabel) return templateLabel;

        const savedComponent = Array.isArray(formData?.components)
          ? formData.components.find((item: any) => {
              const savedKey = getComponentValueKey(item);
              return savedKey.toLowerCase() === componentKey.toLowerCase();
            })
          : null;

        const savedLabel =
          savedComponent?.label ||
          savedComponent?.componentLabel ||
          savedComponent?.name;

        if (savedLabel) return String(savedLabel);
      }

      if (isObjectValue(component)) {
        const directLabel =
          component.label || component.componentLabel || component.name;
        if (directLabel) return String(directLabel);
      }

      return componentKey ? toTitleCase(componentKey) : "-";
    },
    [formData?.components, getOptionLabelForValue],
  );

  const renderPayoutPolicies = useCallback(
    (stepId: string, stepData: any, step?: any): React.ReactNode => {
      const cards = getPayoutPolicyCards(stepData, step);

      if (cards.length === 0) {
        return <div className="text-sm text-gray-500">-</div>;
      }

      const formatPolicyFieldLabel = (
        key: string,
        fallbackPath?: string,
        ...sources: any[]
      ) => {
        const valueLabelOnlyKeys = new Set([
          "selectionMode",
          "appliedPayoutType",
          "mode",
          "policyType",
        ]);

        if (valueLabelOnlyKeys.has(key)) {
          return formatFieldLabel(key, fallbackPath || `${stepId}.${key}`);
        }

        const dynamicLabel = getFirstValue(
          ...sources.map((source) => getLabelFromSource(source, key)),
          getLabelFromSource(step, key),
          getLabelFromSource(step?.outcomeRegistry, key),
        );

        return dynamicLabel
          ? String(dynamicLabel)
          : formatFieldLabel(key, fallbackPath || `${stepId}.${key}`);
      };

      const formatPolicyFieldValue = (
        key: string,
        value: any,
        fieldPath?: string,
        ...sources: any[]
      ): React.ReactNode => {
        const dynamicLabel = getFirstValue(
          ...sources.map((source) => getValueLabelFromSource(source, key, value)),
          getValueLabelFromSource(step, key, value),
          getValueLabelFromSource(step?.outcomeRegistry, key, value),
        );

        return dynamicLabel
          ? String(dynamicLabel)
          : formatFieldValue(value, fieldPath || `${stepId}.${key}`);
      };

      const knownPolicyFields = new Set([
        "key",
        "label",
        "title",
        "name",
        "scenario",
        "scenarioLabel",
        "outcome",
        "outcomeKey",
        "base",
        "baseIncluded",
        "includesBase",
        "baseLocked",
        "baseSelectable",
        "componentsSelectable",
        "optionsFrom",
        "selectionMode",
        "appliedPayoutType",
        "mode",
        "policyType",
        "selectedComponents",
        "components",
        "componentTypes",
        "includedComponents",
        "excludedComponents",
      ]);

      const groupNames = cards
        .map((card) => card.group || "")
        .filter((groupName, index, list) => list.indexOf(groupName) === index);

      return (
        <div className="space-y-4">
          {groupNames.map((groupName, groupIndex) => {
            const groupCards = cards.filter(
              (card) => (card.group || "") === groupName,
            );

            return (
              <div key={groupName || `policy-group-${groupIndex}`} className="space-y-3">
                {groupName && (
                  <div className="flex items-center gap-3">
                    <p className="text-xs font-semibold text-gray-500">
                      {tr(groupName)}
                    </p>
                    <div className="h-px flex-1 bg-gray-200" />
                  </div>
                )}

                {groupCards.map((card) => {
                  const value = card.value || {};
                  const meta: any = card.meta || {};
                  const summary =
                    policySummaryByScenario[card.key.toLowerCase()] ||
                    policySummaryByScenario[
                      String(value.scenario || value.scenarioLabel || "").toLowerCase()
                    ];

                  const baseIncluded = getFirstValue(
                    value.baseIncluded,
                    value.includesBase,
                    value.base?.included,
                    meta.baseIncluded,
                    summary?.baseIncluded,
                  );
                  const baseLocked = getFirstValue(
                    value.baseLocked,
                    value.base?.locked,
                    meta.baseLocked,
                  );
                  const policyMode = getFirstValue(
                    value.selectionMode,
                    value.appliedPayoutType,
                    meta.selectionMode,
                    summary?.appliedPayoutType,
                    value.mode,
                    value.policyType,
                  );
                  const policyModeEntry = getFirstFieldEntry(
                    {
                      key: "selectionMode",
                      value: value.selectionMode,
                      source: value,
                    },
                    {
                      key: "appliedPayoutType",
                      value: value.appliedPayoutType,
                      source: value,
                    },
                    {
                      key: "selectionMode",
                      value: meta.selectionMode,
                      source: meta,
                    },
                    {
                      key: "appliedPayoutType",
                      value: summary?.appliedPayoutType,
                      source: summary,
                    },
                    { key: "mode", value: value.mode, source: value },
                    { key: "policyType", value: value.policyType, source: value },
                  );
                  const selectedComponentsEntry = getFirstFieldEntry(
                    {
                      key: "selectedComponents",
                      value: value.selectedComponents,
                      source: value,
                    },
                    { key: "components", value: value.components, source: value },
                    {
                      key: "componentTypes",
                      value: value.componentTypes,
                      source: value,
                    },
                    {
                      key: "includedComponents",
                      value: value.includedComponents,
                      source: value,
                    },
                    {
                      key: "includedComponents",
                      value: summary?.includedComponents,
                      source: summary,
                    },
                  );
                  const selectedComponentsValue = selectedComponentsEntry?.value;
                  const selectedComponents = Array.isArray(selectedComponentsValue)
                    ? selectedComponentsValue
                    : selectedComponentsValue
                      ? [selectedComponentsValue]
                      : [];
                  const baseLockedDisplay =
                    typeof baseLocked === "boolean"
                      ? getFirstValue(
                          getValueLabelFromSource(value, "baseLocked", baseLocked),
                          getValueLabelFromSource(meta, "baseLocked", baseLocked),
                          getValueLabelFromSource(
                            summary,
                            "baseLocked",
                            baseLocked,
                          ),
                        )
                      : undefined;
                  const policyModeValueLabel = policyModeEntry
                    ? getFirstValue(
                        getValueLabelFromSource(
                          policyModeEntry.source,
                          policyModeEntry.key,
                          policyModeEntry.value,
                        ),
                        getValueLabelFromSource(
                          value,
                          policyModeEntry.key,
                          policyModeEntry.value,
                        ),
                        getValueLabelFromSource(
                          meta,
                          policyModeEntry.key,
                          policyModeEntry.value,
                        ),
                        getValueLabelFromSource(
                          summary,
                          policyModeEntry.key,
                          policyModeEntry.value,
                        ),
                      )
                    : undefined;
                  const selectedComponentsEmptyText = getFirstValue(
                    value.selectedComponentsEmptyLabel,
                    value.selectedComponentsEmptyText,
                    meta.selectedComponentsEmptyLabel,
                    meta.selectedComponentsEmptyText,
                    summary?.selectedComponentsEmptyLabel,
                    summary?.selectedComponentsEmptyText,
                    meta.componentsSelectable === false
                      ? getFirstValue(
                          value.componentsNotSelectableLabel,
                          value.componentsNotSelectableText,
                          meta.componentsNotSelectableLabel,
                          meta.componentsNotSelectableText,
                          summary?.componentsNotSelectableLabel,
                          summary?.componentsNotSelectableText,
                        )
                      : undefined,
                  );

                  const extraFields = Object.entries(value).filter(([key, item]) => {
                    if (knownPolicyFields.has(key)) return false;
                    if (item === null || item === undefined || item === "") return false;
                    if (Array.isArray(item) && item.length === 0) return false;
                    if (isObjectValue(item) && Object.keys(item).length === 0) return false;
                    return true;
                  });

                  return (
                    <div
                      key={`${stepId}-${card.key}`}
                      className="rounded-lg border border-gray-200 bg-gray-50 p-4"
                    >
                      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 break-words">
                            {tr(card.label)}
                          </p>
                        </div>

                        {baseLockedDisplay && (
                          <span
                            className={[
                              "rounded-full border px-2.5 py-1 text-[11px] font-semibold",
                              baseLocked
                                ? "border-blue-200 bg-blue-50 text-blue-700"
                                : "border-gray-200 bg-white text-gray-500",
                            ].join(" ")}
                          >
                            {tr(String(baseLockedDisplay))}
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                        <div className="rounded-md bg-white px-3 py-2">
                          <div className="text-[10px] text-gray-500 leading-4">
                            {formatPolicyFieldLabel(
                              "baseIncluded",
                              "core.baseAmount",
                              value,
                              meta,
                              summary,
                            )}
                          </div>
                          <div className="mt-1 text-sm font-semibold text-gray-900 break-words leading-5">
                            {typeof baseIncluded === "boolean"
                              ? formatPolicyFieldValue(
                                  "baseIncluded",
                                  baseIncluded,
                                  `${stepId}.baseIncluded`,
                                  value,
                                  meta,
                                  summary,
                                )
                              : "-"}
                          </div>
                        </div>

                        <div className="rounded-md bg-white px-3 py-2">
                          <div className="text-[10px] text-gray-500 leading-4">
                            {formatPolicyFieldLabel(
                              policyModeEntry?.key || "mode",
                              "components.mode",
                              policyModeEntry?.source,
                              value,
                              meta,
                              summary,
                            )}
                          </div>
                          <div className="mt-1 text-sm font-semibold text-gray-900 break-words leading-5">
                            {policyModeEntry
                              ? policyModeValueLabel
                                ? String(policyModeValueLabel)
                                : tr(toTitleCase(String(policyModeEntry.value)))
                              : policyMode
                                ? tr(toTitleCase(String(policyMode)))
                                : "-"}
                          </div>
                        </div>

                        <div className="rounded-md bg-white px-3 py-2 md:col-span-2 xl:col-span-1">
                          <div className="text-[10px] text-gray-500 leading-4">
                            {formatPolicyFieldLabel(
                              selectedComponentsEntry?.key || "selectedComponents",
                              "components.componentType",
                              selectedComponentsEntry?.source,
                              value,
                              meta,
                              summary,
                            )}
                          </div>
                          {selectedComponents.length > 0 ? (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {selectedComponents.map((component: any, index: number) => (
                                <span
                                  key={`${getComponentValueKey(component)}-${index}`}
                                  className="rounded border border-sky-200 bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700"
                                >
                                  {getComponentLabel(component)}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <div className="mt-1 text-sm font-semibold text-gray-900 break-words leading-5">
                              {selectedComponentsEmptyText
                                ? tr(String(selectedComponentsEmptyText))
                                : "-"}
                            </div>
                          )}
                        </div>

                        {extraFields.map(([key, item]) => (
                          <div key={key} className="rounded-md bg-white px-3 py-2">
                            <div className="text-[10px] text-gray-500 leading-4">
                              {formatFieldLabel(key, `${stepId}.${key}`)}
                            </div>
                            <div className="mt-1 text-sm font-semibold text-gray-900 break-words leading-5">
                              {formatFieldValue(item, `${stepId}.${key}`)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      );
    },
    [formatFieldLabel, formatFieldValue, getComponentLabel, policySummaryByScenario, tr],
  );

  const renderNestedObjectGrid = useCallback(
    (obj: any, basePath?: string): React.ReactNode => {
      if (!obj || typeof obj !== "object") return "-";

      return (
        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
          {Object.entries(obj).map(([k, v]) => {
            const fieldPath = basePath ? `${basePath}.${k}` : k;
            if (v === null || v === undefined || v === "") return null;
            if (Array.isArray(v) && v.length === 0) return null;
            if (
              typeof v === "object" &&
              !Array.isArray(v) &&
              Object.keys(v).length === 0
            ) {
              return null;
            }
            const renderAsFileValue = shouldRenderFileValue(v, fieldPath);

            return (
              <div
                key={k}
                className={`min-w-0 ${renderAsFileValue ? "col-span-2" : ""}`}
              >
                <div className="text-[10px] text-gray-500 leading-4">
                  {formatFieldLabel(k, fieldPath)}
                </div>
                <div className="mt-1 text-sm font-semibold text-gray-900 break-words leading-5">
                  {formatFieldValue(v, fieldPath)}
                </div>
              </div>
            );
          })}
        </div>
      );
    },
    [formatFieldLabel, formatFieldValue, shouldRenderFileValue],
  );

  const renderDynamicObject = useCallback(
    (obj: any, skip = 0): React.ReactNode => {
      if (!obj || typeof obj !== "object") return "-";

      return (
        <div className="grid grid-cols-2 gap-x-6 gap-y-5">
          {Object.entries(obj)
            .slice(skip)
            .map(([fieldKey, fieldValue]) => {
              const fieldPath = fieldKey;
              if (fieldValue === null || fieldValue === undefined || fieldValue === "") return null;
              if (Array.isArray(fieldValue) && fieldValue.length === 0) return null;
              if (
                typeof fieldValue === "object" &&
                !Array.isArray(fieldValue) &&
                Object.keys(fieldValue).length === 0
              ) {
                return null;
              }

              const step = templateSteps.find(
                (item: any) => getTemplateStepId(item) === fieldKey,
              );
              const hasOutcomeData =
                isObjectValue(fieldValue) &&
                (Array.isArray(fieldValue.outcomes) ||
                  isObjectValue(fieldValue.outcomes) ||
                  Array.isArray(fieldValue.scenarios) ||
                  isObjectValue(fieldValue.scenarios));
              const isPayoutPolicyStep =
                !!step?.outcomeRegistry ||
                /payout\s*policies/i.test(String(step?.title || "")) ||
                (/polic/i.test(fieldKey) && hasOutcomeData);
              const renderAsFileValue = shouldRenderFileValue(
                fieldValue,
                fieldPath,
              );

              if (isPayoutPolicyStep) {
                return (
                  <div key={fieldKey} className="col-span-2">
                    <div className="text-sm text-gray-500 mb-2 leading-5">
                      {formatFieldLabel(fieldKey, fieldPath)}
                    </div>
                    {renderPayoutPolicies(fieldKey, fieldValue, step)}
                  </div>
                );
              }

              if (renderAsFileValue) {
                return (
                  <div key={fieldKey} className="col-span-2 min-w-0">
                    <div className="text-sm text-gray-500 leading-5">
                      {formatFieldLabel(fieldKey, fieldPath)}
                    </div>
                    <div className="mt-1 text-sm font-semibold text-gray-900 break-words leading-5">
                      {formatFieldValue(fieldValue, fieldPath)}
                    </div>
                  </div>
                );
              }

              if (Array.isArray(fieldValue)) {
                const allPrimitive = fieldValue.every(
                  (item) =>
                    item === null ||
                    item === undefined ||
                    ["string", "number", "boolean"].includes(typeof item),
                );

                return (
                  <div
                    key={fieldKey}
                    className="col-span-2"
                    id={fieldKey === "documentRequirement" ? "document-requirement-section" : undefined}
                  >
                    <div className="text-sm text-gray-500 mb-2 leading-5">
                      {formatFieldLabel(fieldKey, fieldPath)}
                    </div>

                    {allPrimitive ? (
                      <div className="text-sm font-semibold text-gray-900 leading-5 break-words">
                        {fieldValue.map((item) => formatFieldValue(item, fieldPath)).join(", ")}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {fieldValue.map((arrayItem, arrayIndex) => {
                          const itemKey = getItemKey(fieldPath, arrayIndex);
                          return (
                            <div
                              key={arrayIndex}
                              className="rounded-lg border border-gray-200 bg-gray-50 p-4"
                            >
                              {typeof arrayItem === "object" && arrayItem !== null ? (
                    <>
                      {renderNestedObjectGrid(arrayItem, fieldPath)}
                      {isDocumentUploadAllowed &&
                      typeof arrayItem.documentType === "string" &&
                      arrayItem.documentType.trim() !== "" &&
                      (arrayItem.uploadStatus === "PENDING" ||
                        !arrayItem.uploadStatus) ? (
                        <div className="mt-4 rounded-lg border border-dashed border-gray-200 bg-white p-4">
                          <div className="text-sm font-semibold text-gray-900">
                            {t("uploadRequiredDocument", { defaultValue: "Upload required document" })}
                          </div>
                          <div className="mt-1 text-sm text-gray-500">
                            {t("chooseFileAndUploadForDeal", { defaultValue: "Choose a file and upload it for this deal." })}
                          </div>
                          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                            <input
                              key={itemKey}
                              type="file"
                              accept="application/pdf,.pdf"
                              onClick={(event) => {
                                (event.target as HTMLInputElement).value = "";
                              }}
                              onChange={(event) =>
                                handleFileChange(event, itemKey)
                              }
                              className="block text-sm text-gray-600"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                handleUploadDocument(
                                  itemKey,
                                  String(arrayItem.documentType || ""),
                                )
                              }
                              disabled={uploadingByKey[itemKey]}
                              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
                            >
                              {uploadingByKey[itemKey]
                                ? t("uploadingFile", { defaultValue: "Uploading..." })
                                : t("uploadButton", { defaultValue: "Upload" })}
                            </button>
                            {selectedFilesByKey[itemKey] && (
                              <button
                                type="button"
                                onClick={() =>
                                  setSelectedFilesByKey((prev) => ({
                                    ...prev,
                                    [itemKey]: null,
                                  }))
                                }
                                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                              >
                                {t("remove", { defaultValue: "Remove" })}
                              </button>
                            )}
                          </div>
                        </div>
                      ) : null}
                    </>
                  ) : (
                                <div className="text-sm font-semibold text-gray-900 leading-5">
                                  {formatFieldValue(arrayItem, fieldPath)}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              if (typeof fieldValue === "object") {
                return (
                  <div key={fieldKey} className="col-span-2">
                    <div className="text-sm text-gray-500 mb-2 leading-5">
                      {formatFieldLabel(fieldKey, fieldPath)}
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                      {renderNestedObjectGrid(fieldValue, fieldPath)}
                    </div>
                  </div>
                );
              }

              return (
                <div key={fieldKey} className="min-w-0">
                  <div className="text-sm text-gray-500 leading-5">
                    {formatFieldLabel(fieldKey, fieldPath)}
                  </div>
                  <div className="mt-1 text-sm font-semibold text-gray-900 break-words leading-5">
                    {formatFieldValue(fieldValue, fieldPath)}
                  </div>
                </div>
              );
            })}
        </div>
      );
    },
    [
      formatFieldLabel,
      formatFieldValue,
      renderNestedObjectGrid,
      renderPayoutPolicies,
      shouldRenderFileValue,
      templateSteps,
      isDocumentUploadAllowed,
      selectedFilesByKey,
      uploadingByKey,
      handleFileChange,
      handleUploadDocument,
      t,
    ],
  );

  const renderSummary = useCallback(
    (obj: any): React.ReactNode => {
      if (!obj || Object.keys(obj).length === 0) {
        return <div className="text-sm text-gray-500">{t("noSummaryData")}</div>;
      }

      const entries = Object.entries(obj).slice(0, 3);

      return (
        <div className="space-y-4">
          {entries.map(([groupKey, groupValue]) => (
            <div key={groupKey} className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="text-xs text-gray-500 mb-3">
                {formatFieldLabel(groupKey, groupKey)}
              </div>

              {typeof groupValue === "object" &&
              groupValue !== null &&
              !Array.isArray(groupValue) ? (
                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                  {Object.entries(groupValue).map(([k, v]) => {
                    const fieldPath = `${groupKey}.${k}`;
                    if (v === null || v === undefined || v === "") return null;
                    if (Array.isArray(v) && v.length === 0) return null;
                    if (
                      typeof v === "object" &&
                      !Array.isArray(v) &&
                      Object.keys(v).length === 0
                    ) {
                      return null;
                    }
                    const renderAsFileValue = shouldRenderFileValue(v, fieldPath);

                    return (
                      <div
                        key={k}
                        className={`min-w-0 ${
                          renderAsFileValue ? "col-span-2" : ""
                        }`}
                      >
                        <div className="text-[10px] text-gray-500 leading-4">
                          {formatFieldLabel(k, fieldPath)}
                        </div>
                        <div className="mt-1 text-sm font-semibold text-gray-900 break-words leading-5">
                          {formatFieldValue(v, fieldPath)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-sm font-semibold text-gray-900 leading-5 break-words">
                  {formatFieldValue(groupValue, groupKey)}
                </div>
              )}
            </div>
          ))}
        </div>
      );
    },
    [formatFieldLabel, formatFieldValue, shouldRenderFileValue, t],
  );

  return {
    renderDynamicObject,
    renderSummary,
    tr,
  };
};